import { useState } from 'react';
import PageError from '../PageError';
import { motion } from 'framer-motion';
import {
  Bus,
  User,
  Route,
  MapPin,
  AlertTriangle,
  Search,
  X,
  Filter,
  Eye,
  Edit,
  Trash2,
  MoreHorizontal,
} from 'lucide-react';
import { StatusBadge } from '../ui/StatusBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdownMenu';

/**
 * Composant de statistiques et vue d'ensemble des assignations
 * Affiche un dashboard complet des assignations actuelles
 */
const AssignmentStats = ({
  routes = [],
  buses = [],
  drivers = [],
  stops = [],
  onViewRoute,
  onEditRoute,
  onDeleteRoute,
  error = null,
  onRetry,
}) => {
  // État pour le filtre de recherche
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // Calculer les statistiques détaillées
  const detailedStats = {
    // Bus — buses du hook sont déjà filtrés sur status=active, enrichis avec hasAssignedRoute
    totalBuses: buses.length,
    assignedBuses: buses.filter((bus) => bus.hasAssignedRoute).length,
    availableBuses: buses.filter((bus) => !bus.hasAssignedRoute).length,

    // Chauffeurs — même pattern
    totalDrivers: drivers.length,
    assignedDrivers: drivers.filter((driver) => driver.hasAssignedRoute).length,
    availableDrivers: drivers.filter((driver) => !driver.hasAssignedRoute).length,

    // Circuits
    totalRoutes: routes.length,
    activeRoutes: routes.filter((route) => route.status === 'active').length,
    completeRoutes: routes.filter((route) => route.bus_id && route.driver_id).length,

    // Arrêts — stops enrichis avec isAssigned (pas route_id direct)
    totalStops: stops.length,
    assignedStops: stops.filter((stop) => stop.isAssigned).length,
    unassignedStops: stops.filter((stop) => !stop.isAssigned).length,
  };

  // Calculer les pourcentages
  const percentages = {
    busUtilization: detailedStats.totalBuses > 0 ? (detailedStats.assignedBuses / detailedStats.totalBuses) * 100 : 0,
    driverUtilization:
      detailedStats.totalDrivers > 0 ? (detailedStats.assignedDrivers / detailedStats.totalDrivers) * 100 : 0,
    routeCompletion:
      detailedStats.totalRoutes > 0 ? (detailedStats.completeRoutes / detailedStats.totalRoutes) * 100 : 0,
    stopCoverage: detailedStats.totalStops > 0 ? (detailedStats.assignedStops / detailedStats.totalStops) * 100 : 0,
  };

  // Cartes de statistiques principales
  const mainCards = [
    {
      title: 'Bus',
      total: detailedStats.totalBuses,
      assigned: detailedStats.assignedBuses,
      available: detailedStats.availableBuses,
      percentage: percentages.busUtilization,
      icon: Bus,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      barColor: 'bg-blue-500',
    },
    {
      title: 'Chauffeurs',
      total: detailedStats.totalDrivers,
      assigned: detailedStats.assignedDrivers,
      available: detailedStats.availableDrivers,
      percentage: percentages.driverUtilization,
      icon: User,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200',
      barColor: 'bg-green-500',
    },
    {
      title: 'Circuits',
      total: detailedStats.totalRoutes,
      assigned: detailedStats.completeRoutes,
      available: detailedStats.totalRoutes - detailedStats.completeRoutes,
      percentage: percentages.routeCompletion,
      icon: Route,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      barColor: 'bg-purple-500',
    },
    {
      title: 'Arrêts',
      total: detailedStats.totalStops,
      assigned: detailedStats.assignedStops,
      available: detailedStats.unassignedStops,
      percentage: percentages.stopCoverage,
      icon: MapPin,
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      barColor: 'bg-orange-500',
    },
  ];

  // Circuits avec problèmes
  const problematicRoutes = routes.filter((route) => route.status === 'active' && (!route.bus_id || !route.driver_id));

  // Circuits à afficher (tous les circuits, le filtre de statut gère l'affichage)
  const completeRoutes = routes;

  return (
    <div className="space-y-6">
      {/* Bloc blanc principal — masqué en cas d'erreur */}
      {!error && (
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          {/* Titre */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Vue d'Ensemble des Assignations</h2>
            <p className="text-gray-600">Gestion centralisée des assignations bus, chauffeurs, circuits et arrêts</p>
          </div>

          {/* Cartes de statistiques principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`${card.bgColor} ${card.borderColor} border rounded-lg p-6`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2 rounded-lg bg-white ${card.iconColor}`}>
                    <card.icon size={24} />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{card.total}</div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Assignés:</span>
                    <span className="font-medium text-gray-900">{card.assigned}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Disponibles:</span>
                    <span className="font-medium text-gray-900">{card.available}</span>
                  </div>

                  {/* Barre de progression */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Utilisation</span>
                      <span className="font-medium">{card.percentage.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${card.barColor} transition-all duration-500`}
                        style={{ width: `${Math.min(card.percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Alertes et circuits problématiques — masquées en cas d'erreur */}
      {!error && problematicRoutes.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="text-orange-600" size={20} />
            <h3 className="font-semibold text-orange-900">Circuits Incomplets</h3>
            <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
              {problematicRoutes.length}
            </span>
          </div>

          <div className="space-y-2">
            {problematicRoutes.slice(0, 5).map((route) => (
              <div key={route.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                <div>
                  <div className="font-medium text-gray-900">{route.route_name || route.name}</div>
                  <div className="text-sm text-gray-600">{route.description}</div>
                </div>
                <div className="flex space-x-2 text-xs">
                  {!route.bus_id && <span className="bg-red-100 text-red-700 px-2 py-1 rounded">Pas de bus</span>}
                  {!route.driver_id && (
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded">Pas de chauffeur</span>
                  )}
                </div>
              </div>
            ))}
            {problematicRoutes.length > 5 && (
              <div className="text-sm text-orange-700 text-center">
                Et {problematicRoutes.length - 5} autres circuits...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filtre + tableau — toujours affiché (erreur inline à l'intérieur si besoin) */}
      {(completeRoutes.length > 0 || !!error) && (
        <>
          {/* Filtres */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Barre de recherche */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom de circuit, bus ou chauffeur..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {searchFilter && (
                    <button
                      onClick={() => setSearchFilter('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filtres de statut */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filtres:</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { value: 'all', label: 'Tous', active: 'bg-blue-100 text-blue-800' },
                    { value: 'active', label: 'Actif', active: 'bg-green-100 text-green-800' },
                    { value: 'inactive', label: 'Inactif', active: 'bg-gray-100 text-gray-800' },
                  ].map(({ value, label, active }) => (
                    <button
                      key={value}
                      onClick={() => setStatusFilter(value)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${statusFilter === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tableau / Cards */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {error && <PageError error={error} onRetry={onRetry} variant="inline" />}
            {!error && (
              <>
                {/* ── Vue cartes (mobile < md) ── */}
                <div className="md:hidden divide-y divide-gray-100">
                  {completeRoutes
                    .filter((route) => {
                      const sl = searchFilter.toLowerCase();
                      const routeName = (route.route_name || route.name || '').toLowerCase();
                      const busFromRoute = route.buses ?? route.bus;
                      const assignedBus = buses.find((b) => b.id === (route.bus_id ?? route.assigned_bus_id));
                      const busDisplay = (
                        busFromRoute?.bus_number ??
                        busFromRoute?.license_plate ??
                        assignedBus?.bus_number ??
                        assignedBus?.busNumber ??
                        ''
                      ).toLowerCase();
                      const assignedDriver = drivers.find(
                        (d) => d.id === (route.driver_id ?? route.assigned_driver_id),
                      );
                      const driverName = assignedDriver
                        ? `${assignedDriver.first_name ?? ''} ${assignedDriver.last_name ?? ''}`.trim().toLowerCase()
                        : '';
                      const matchesSearch =
                        routeName.includes(sl) || busDisplay.includes(sl) || driverName.includes(sl);
                      const matchesStatus =
                        statusFilter === 'all' || (route.status?.toLowerCase() ?? 'active') === statusFilter;
                      return matchesSearch && matchesStatus;
                    })
                    .map((route) => {
                      const busFromRoute = route.buses ?? route.bus;
                      const assignedBus = buses.find((b) => b.id === (route.bus_id ?? route.assigned_bus_id));
                      const busDisplay =
                        busFromRoute?.bus_number ??
                        busFromRoute?.license_plate ??
                        assignedBus?.bus_number ??
                        assignedBus?.busNumber ??
                        assignedBus?.license_plate ??
                        '';
                      const hasBus = (route.bus_id ?? route.assigned_bus_id) && busDisplay;
                      const assignedDriver = drivers.find(
                        (d) => d.id === (route.driver_id ?? route.assigned_driver_id),
                      );
                      const hasDriver = assignedDriver && (assignedDriver.first_name || assignedDriver.last_name);
                      return (
                        <motion.div
                          key={route.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 hover:bg-gray-50"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                <Route size={20} className="text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {route.route_name || route.name}
                                </div>
                                <div className="text-xs text-gray-500 font-mono">
                                  {route.route_code || route.code || 'N/A'}
                                </div>
                              </div>
                            </div>
                            <StatusBadge status={route.status} />
                          </div>
                          <div className="mt-2.5 ml-[52px] space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              {hasBus ? (
                                <>
                                  <Bus size={13} className="text-gray-400 shrink-0" />
                                  <span className="text-sm text-gray-600">{busDisplay}</span>
                                </>
                              ) : (
                                <>
                                  <AlertTriangle size={13} className="text-orange-400 shrink-0" />
                                  <span className="text-sm text-gray-400">Pas de bus</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {hasDriver ? (
                                <>
                                  <User size={13} className="text-gray-400 shrink-0" />
                                  <span className="text-sm text-gray-600">
                                    {[assignedDriver.first_name, assignedDriver.last_name].filter(Boolean).join(' ')}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <AlertTriangle size={13} className="text-orange-400 shrink-0" />
                                  <span className="text-sm text-gray-400">Pas de chauffeur</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin size={13} className="text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-600">
                                {route.stopsCount ?? route.stops?.length ?? 0} arrêt(s)
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                </div>

                {/* ── Tableau (desktop ≥ md) ── */}
                <div className="overflow-x-auto hidden md:block">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Circuit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assignation
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Arrêts
                        </th>
                        <th className="px-4 py-3 w-[1%] whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-4 py-3 w-[1%] whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {completeRoutes
                        .filter((route) => {
                          // Filtre de recherche (bus_number depuis route.buses si jointure API, sinon liste buses)
                          const searchLower = searchFilter.toLowerCase();
                          const routeName = (route.route_name || route.name || '').toLowerCase();
                          const busFromRoute = route.buses ?? route.bus;
                          const routeBusId = route.bus_id ?? route.assigned_bus_id;
                          const assignedBus = buses.find((bus) => bus.id === routeBusId);
                          const busDisplayForFilter =
                            busFromRoute?.bus_number ??
                            busFromRoute?.license_plate ??
                            (assignedBus
                              ? (assignedBus.bus_number ??
                                assignedBus.busNumber ??
                                assignedBus.number ??
                                assignedBus.license_plate ??
                                assignedBus.licensePlate ??
                                '')
                              : '');
                          const busNumber = busDisplayForFilter.toLowerCase();
                          const assignedDriver = drivers.find(
                            (driver) => driver.id === (route.driver_id ?? route.assigned_driver_id),
                          );
                          const driverName = assignedDriver
                            ? `${assignedDriver.first_name ?? ''} ${assignedDriver.last_name ?? ''}`
                                .trim()
                                .toLowerCase()
                            : '';

                          const matchesSearch =
                            routeName.includes(searchLower) ||
                            busNumber.includes(searchLower) ||
                            driverName.includes(searchLower);

                          // Filtre de statut (même valeurs que le dropdown du formulaire)
                          const routeStatus = route.status?.toLowerCase() ?? 'active';
                          const matchesStatus = statusFilter === 'all' || routeStatus === statusFilter;

                          return matchesSearch && matchesStatus;
                        })
                        .map((route) => {
                          // Bus : priorité au bus récupéré depuis l'API (jointure route.bus_id → buses.bus_number)
                          const busFromRoute = route.buses ?? route.bus;
                          const assignedBusFromList = buses.find(
                            (bus) => bus.id === (route.bus_id ?? route.assigned_bus_id),
                          );
                          const busDisplay =
                            busFromRoute?.bus_number ??
                            busFromRoute?.license_plate ??
                            (assignedBusFromList
                              ? (assignedBusFromList.bus_number ??
                                assignedBusFromList.busNumber ??
                                assignedBusFromList.number ??
                                assignedBusFromList.license_plate ??
                                assignedBusFromList.licensePlate ??
                                '')
                              : '');
                          const hasBusAssigned = (route.bus_id ?? route.assigned_bus_id) && busDisplay !== '';
                          const assignedDriver = drivers.find(
                            (driver) => driver.id === (route.driver_id ?? route.assigned_driver_id),
                          );

                          return (
                            <tr key={route.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                    <Route size={20} className="text-blue-600" />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {route.route_name || route.name}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-900 font-mono">
                                  {route.route_code || route.code || 'N/A'}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                  {hasBusAssigned ? (
                                    <>
                                      <Bus size={14} className="text-gray-400 shrink-0" />
                                      <span>{busDisplay}</span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                                      <span>Pas de bus assigné</span>
                                    </>
                                  )}
                                </div>
                                <div className="text-sm text-gray-900 flex items-center gap-2 mt-1">
                                  {assignedDriver && (assignedDriver.first_name || assignedDriver.last_name) ? (
                                    <>
                                      <User size={14} className="text-gray-400 shrink-0" />
                                      <span>
                                        {[assignedDriver.first_name, assignedDriver.last_name]
                                          .filter(Boolean)
                                          .join(' ')}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                                      <span>Pas de chauffeur assigné</span>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                  <MapPin size={14} className="text-gray-400 shrink-0" />
                                  <span>{route.stopsCount ?? route.stops?.length ?? 0}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <StatusBadge status={route.status} />
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      aria-label="Actions"
                                      className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                    >
                                      <MoreHorizontal size={16} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem onClick={() => onViewRoute?.(route)}>
                                      <Eye size={14} className="mr-2" />
                                      Voir les arrêts
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => onEditRoute?.(route)}>
                                      <Edit size={14} className="mr-2" />
                                      Modifier
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => onDeleteRoute?.(route)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <Trash2 size={14} className="mr-2" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {completeRoutes.filter((route) => {
                  const searchLower = searchFilter.toLowerCase();
                  const routeName = (route.route_name || route.name || '').toLowerCase();
                  const busFromRoute = route.buses ?? route.bus;
                  const routeBusId = route.bus_id ?? route.assigned_bus_id;
                  const assignedBus = buses.find((bus) => bus.id === routeBusId);
                  const busDisplayForFilter =
                    busFromRoute?.bus_number ??
                    busFromRoute?.license_plate ??
                    (assignedBus
                      ? (assignedBus.bus_number ??
                        assignedBus.busNumber ??
                        assignedBus.number ??
                        assignedBus.license_plate ??
                        assignedBus.licensePlate ??
                        '')
                      : '');
                  const busNumber = busDisplayForFilter.toLowerCase();
                  const assignedDriver = drivers.find(
                    (driver) => driver.id === (route.driver_id ?? route.assigned_driver_id),
                  );
                  const driverName = assignedDriver
                    ? `${assignedDriver.first_name ?? ''} ${assignedDriver.last_name ?? ''}`.trim().toLowerCase()
                    : '';
                  const matchesSearch =
                    routeName.includes(searchLower) ||
                    busNumber.includes(searchLower) ||
                    driverName.includes(searchLower);
                  const routeStatus = route.status?.toLowerCase() ?? 'active';
                  const matchesStatus = statusFilter === 'all' || routeStatus === statusFilter;
                  return matchesSearch && matchesStatus;
                }).length === 0 && (
                  <div className="p-8 text-center">
                    <Route className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun circuit trouvé</h3>
                    <p className="text-gray-600">Ajustez les filtres ou créez un nouveau circuit.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssignmentStats;
